export const omitDeep = ( obj, key ) => {
    const keys = Object.keys( obj );
    const newObj = {};
    keys.forEach( ( i ) => {
        if( i !== key ){
            const val = obj[ i ];
            if( val instanceof Date ){
                newObj[ i ] = val;
            } else if( Array.isArray( val ) ){
                newObj[ i ] = omitDeepArrayWalk( val, key );
            } else if( typeof val === 'object' && val !== null ){
                newObj[ i ] = omitDeep( val, key );
            } else {
                newObj[ i ] = val;
            }
        }
    } );
    return newObj;
};

export const omitDeepArrayWalk = ( arr, key ) => {
    return arr.map( ( val ) => {
        if( Array.isArray( val ) ){
            return omitDeepArrayWalk( val, key )
        } else if( typeof val === 'object' ) return omitDeep( val, key );
        return val
    } )
};

export const updateDeep = ( obj, path, value, clone = false, separator = '.' ) => {

    let keyPath = ( typeof path === 'string' ) ? path.split( separator ) : path;

    let lastKeyIndex = keyPath.length - 1;

    let result = clone ? Object.assign( {}, obj ) : obj,
        key;

    for( let i = 0; i < lastKeyIndex; ++i ){

        key = keyPath[ i ];

        // choose if nested object is array or hash based on if key is number
        if( !( key in result ) ) result[ key ] = parseInt( key ) !== parseInt( key ) ? {} : []

        result = obj[ key ];
    }

    result[ keyPath[ lastKeyIndex ] ] = value;

    return result;
}

export const mergeDeep = ( target: object, ...sources: object[] ) => {

    // object identity inspection
    const isObject = ( item ) => {
        return ( item && typeof item === 'object' && !Array.isArray( item ) );
    }

    // no sources? return original object
    if( !sources.length ) return target;

    // pull first source
    const source = sources.shift();

    // if we have two objects...
    if( isObject( target ) && isObject( source ) ){

        // iteate over the rouce keys
        for( const key in source ){

            // is value of key an object in source?
            if( isObject( source[ key ] ) ){

                // yes; if not available, put into target
                if( !target[ key ] ){

                    Object.assign( target, {
                        [ key ]: {}
                    } );
                }

                // ...then, recurse on that key
                mergeDeep( target[ key ], source[ key ] );

            } else {

                // no, assign the value.
                Object.assign( target, {
                    [ key ]: source[ key ]
                } );
            }
        }
    }

    // recurse over sources
    return mergeDeep( target, ...sources );
}

// Merges two or more source objects such that the original target is not changed
export const mergeDeepImmutable = ( target: object, ...sources: object[] ): object => {

    // Helper function to check if a value is an object
    const isObject = ( item: any ) => item && typeof item === 'object' && !Array.isArray( item );

    // Base case: if no sources, return a shallow copy of target
    if( !sources.length ) return { ...target };

    // Pull first source
    const source = sources.shift();

    // Create a new target copy
    let newTarget = { ...target };

    if( isObject( newTarget ) && isObject( source ) ){

        for( const key in source ){

            if( Array.isArray( source[ key ] ) && Array.isArray( newTarget[ key ] ) ){

                // Merge arrays instead of replacing
                newTarget[ key ] = [ ...newTarget[ key ], ...source[ key ] ];

            } else if( isObject( source[ key ] ) ){

                // Recursively merge objects
                newTarget[ key ] = mergeDeepImmutable( newTarget[ key ] || {}, source[ key ] );

            } else {
                // Assign primitive values and functions
                newTarget[ key ] = source[ key ];
            }
        }
    }

    // Continue merging the remaining sources
    return mergeDeepImmutable( newTarget, ...sources );
};




/**
 * Merges objects deeply while retaining keys from the source that aren't in the target.
 * Unlike standard deep merge, this preserves properties in the source that aren't specified in the target.
 *
 * Example:
 * source = { a: { x: 1, y: 2 }, b: { z: 0 } }
 * target = { a: { y: 3 }, b: { w: 3 } }
 * result = { a: { x: 1, y: 3 }, b: { z: 0, w: 3 } }
 *
 * @param source - The source object whose properties should be retained
 * @param target - The target object whose properties should override source properties
 * @param additionalTargets - Additional target objects to merge
 * @returns A new object with merged properties
 */
export const mergeDeepRetainKeys = ( source: Record<string, any>, target: Record<string, any>, ...additionalTargets: Record<string, any>[] ): Record<string, any> => {
    // Helper function to check if a value is an object
    const isObject = ( item: any ): boolean => item && typeof item === 'object' && !Array.isArray( item );

    // Create a new source copy
    const newSource = { ...source };

    // If no target, return a shallow copy of source
    if( !target ) return newSource;

    // Process the first target
    if( isObject( newSource ) && isObject( target ) ){
        for( const key in target ){
            if( isObject( target[ key ] ) && isObject( newSource[ key ] ) ){
                // Recursively merge objects
                newSource[ key ] = mergeDeepRetainKeys( newSource[ key ], target[ key ] );
            } else if( Array.isArray( target[ key ] ) && Array.isArray( newSource[ key ] ) ){
                // For arrays, replace with target array
                // This behavior can be modified if needed
                newSource[ key ] = [ ...target[ key ] ];
            } else {
                // Assign primitive values and functions
                newSource[ key ] = target[ key ];
            }
        }
    }

    // Continue merging the remaining targets if any
    if( additionalTargets.length ){
        // @ts-ignore
        return mergeDeepRetainKeys( newSource, ...additionalTargets );
    }

    return newSource;
};

export const getPaths = ( object, path = "$", separator = '.', withIndex = true ) => {

    return Object.entries( object ).flatMap( function( o, i ){

        if( typeof o[ 1 ] === "object" && !o[ 1 ].length ){

            return `${ getPaths( o[ 1 ], path + separator + o[ 0 ] ) }`.split( ',' );

        } else if( typeof o[ 1 ] === "object" && o[ 1 ].length ){

            return Object.entries( o[ 1 ] ).flatMap( ( no, i ) => getPaths( no[ 1 ], `${ path }.${ o[ 0 ] }${ withIndex ? `[${ i }]` : '' }` ) );

        } else {

            return `${ path }.${ o[ 0 ] }`;
        }
    } );
}

/*!
 * Add items to an object at a specific path
 * (c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Object}       obj  The object
 * @param  {String|Array} path The path to assign the value to
 * @param  {*}            val  The value to assign
 */
export const putOnPath = ( obj, path, val ) => {

    /**
     * If the path is a string, convert it to an array
     * @param  {String|Array} path The path
     * @return {Array}             The path array
     */
    function stringToPath( path ){

        // If the path isn't a string, return it
        if( typeof path !== 'string' ) return path;

        // Create new array
        let output = [];

        // Split to an array with dot notation
        path.split( '.' ).forEach( function( item ){

            // Split to an array with bracket notation
            item.split( /\[([^}]+)\]/g ).forEach( function( key ){

                // Push to the new array
                if( key.length > 0 ){
                    output.push( key );
                }

            } );

        } );

        return output;

    }

    // Convert the path to an array if not already
    path = stringToPath( path );

    // Cache the path length and current spot in the object
    let length = path.length;
    let current = obj;

    // Loop through the path
    path.forEach( function( key, index ){

        // Check if the assigned key should be an array
        let isArray = key.slice( -2 ) === '[]';

        // If so, get the true key name by removing the trailing []
        key = isArray ? key.slice( 0, -2 ) : key;

        // If the key should be an array and isn't, create an array
        if( isArray && !Array.isArray( current[ key ] ) ){
            current[ key ] = [];
        }

        // If this is the last item in the loop, assign the value
        if( index === length - 1 ){

            // If it's an array, push the value
            // Otherwise, assign it
            if( isArray ){
                current[ key ].push( val );
            } else {
                current[ key ] = val;
            }
        }

        // Otherwise, update the current place in the object
        else {

            // If the key doesn't exist, create it
            if( !current[ key ] ){
                current[ key ] = {};
            }

            // Update the current place in the object
            current = current[ key ];

        }

    } );

}
